import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose, IN_APP_PRODUCT_TYPE, MODULE_TYPE } from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import EnrollButton from '../EnrollButton';
import CourseProp from '../propTypes/courseProp';
import CertificateDisplay from './CertificateDisplay';
import AppProductProp from '../../profile/propTypes/appProductProp';
import PrintModal from '../../profile/mainPanel/print/PrintModal';
import EnrollModal from '../EnrollModal';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../profile/propTypes/StripeCardProp';
import CustomIframe from './CustomIframe';
import { useScrollAnchor } from '../../../services/customHooks';
import Loader from '../../../services/loader';
import Notifications from '../../../services/notifications';
import SocketClient from '../../../services/socketClient';
import RestartCourseModal from '../modal/RestartCourseModal';

const firstCertificate = course =>
  (course.modulesList || []).find(m => m.type === MODULE_TYPE.CERTIFICATE);

function ScormDetail(props) {
  const {
    t,
    _csrf,
    showEnrollOverlay,
    onEnroll,
    course: courseDetails,
    title,
    url,
    appProducts,
    cards,
    countries,
    shouldScrollContent,
    inJourney,
    isFromSession,
    sessionContext,
    journeyContext,
  } = props;
  const loader = useRef(new Loader('#scormContentLayout'));
  const frameId = '#scormFrame';
  const frameIdEl = useRef(null);
  const [course, setCourse] = useState(courseDetails);
  const [showCloseCourse, setShowCloseCourse] = useState(false);
  const closeCourseBtnRef = useRef(null);
  const [isLoading, setLoading] = useState(inJourney && courseDetails.isFromExternalProvider);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [urlUser, setUrlUser] = useState(url);
  const [scormLoaded, setScormLoaded] = useState(false);
  const [showRestartCourseModal, setShowRestartCourseModal] = useState(false);

  const { contentElement, scrollToContent } = useScrollAnchor();

  const getScormUserLink = () => new Promise((resolve, reject) => {
    axios.post(
      `/api/academy/course/scorm/${course._id}/link/preview`,
      {
        redirectUrl: `${window.location.origin}/academy/${course.slug}/completed`,
      },
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data }) => {
        resolve(data.data);
      })
      .catch(err => {
        reject(err);
      });
  });

  const getCourseDetails = () => new Promise((resolve, reject) => {
    const params = {};
    if (sessionContext) {
      params.sessionContext = sessionContext;
    }
    if (journeyContext) {
      params.journeyContext = journeyContext;
    }
    axios.get(
      `/api/academy/course/${course._id}`,
      {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data }) => {
        resolve(data.data);
      })
      .catch(err => {
        reject(err);
      });
  });

  useEffect(
    () => {
      if (loader.current) {
        if (isLoading) {
          loader.current.show();
        } else {
          loader.current.hide();
        }
      }
    },
    [isLoading],
  );
  const handleCourseStatusChange = courseProviderStatus => {
    const { course: courseId, completionStatus, score, successStatus, externalProviderId: scormId } = courseProviderStatus;
    console.log('### Socket.io | SCORM Status: ', { completionStatus, successStatus, score, courseId, scormId });
    if (courseProviderStatus.course === course._id) {
      if (courseProviderStatus.completionStatus === 'completed') {
        if (!course.isManualComplete) {
          setUrlUser(`${window.location.origin}/academy/${course.slug}/completed`);
        } else {
          setShowCloseCourse(true);
          closeCourseBtnRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  useEffect(() => {
    if (course.certificate && course.certificate.isActive && firstCertificate(course)) {
      setSelectedCertificate(firstCertificate(course).certificate);
    } else {
      setSelectedCertificate(null);
    }
    if (!showEnrollOverlay) {
      getScormUserLink()
        .then(providerURL => {
          setLoading(false);
          setUrlUser(providerURL);
        })
        .catch(err => {
          setLoading(false);
          console.error(err);
          Notifications.showErrorFromRequest(err, 'error_fetchCourse');
        });
    }
    SocketClient.on('COURSE_SCORM', handleCourseStatusChange);
  }, [course]);

  const handleCloseCourse = useCallback(() => {
    setUrlUser(`${window.location.origin}/academy/${course.slug}/completed`);
    setShowCloseCourse(false);
  }, [setUrlUser, setShowCloseCourse]);

  const scrollToLesson = () => {
    scrollToContent();
  };

  const prevShouldScrollContent = useRef(shouldScrollContent);
  useEffect(
    () => {
      if (shouldScrollContent !== prevShouldScrollContent.current) {
        prevShouldScrollContent.current = shouldScrollContent;
        scrollToLesson();
      }
    },
    [shouldScrollContent],
  );

  const handleClickLoadCourse = () => {
    setScormLoaded(true);
  };

  const handleClickRestartCourse = () => {
    setShowRestartCourseModal(true);
  };

  const resetCourseSCORM = () => new Promise((resolve, reject) => {
    axios.delete(
      `/api/academy/course/scorm/${course._id}/reset`,
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data }) => {
        resolve(data.data);
      })
      .catch(err => {
        reject(err);
      });
  });

  const restartCourse = () => {
    setLoading(true);
    resetCourseSCORM()
      .then(() => getCourseDetails()
        .then(updatedCourse => {
          setLoading(false);
          setCourse(updatedCourse);
          setScormLoaded(true);
        }))
      .catch(err => {
        setLoading(false);
        console.error(err);
        Notifications.showErrorFromRequest(err, 'error_fetchCourse');
      });
  };

  const handleClickFullScreen = () => {
    if (frameIdEl) {
      frameIdEl.current.requestFullScreenMode();
    }
  };

  const getTitleWelcomeScreen = status => {
    switch (status) {
      case 'un_started':
        return t('academy.detail.scorm.title_start');
      case 'in_progress':
        return t('academy.detail.scorm.title_continue');
      case 'finished':
        return t('academy.detail.scorm.title_restart');
      default:
        return t('academy.detail.scorm.title_start');
    }
  };

  return (
    <>
      {showPrintModal && (
        <PrintModal
          certificateId={selectedCertificate._id}
          cards={cards}
          countries={countries}
          appProduct={appProducts.find(
            p => p.productType === IN_APP_PRODUCT_TYPE.CERTIFICATE,
          )}
          product
          onClose={() => setShowPrintModal(false)}
        />
      )}
      {showEnrollModal && (
        <EnrollModal
          itemType={IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE}
          itemId={selectedCertificate._id}
          onClose={() => setShowEnrollModal(false)}
        />
      )}
      {showRestartCourseModal && (
        <RestartCourseModal
          onConfirm={() => {
            restartCourse();
            setShowRestartCourseModal(false);
          }}
          onClose={() => {
            setShowRestartCourseModal(false);
          }}
        />
      )}
      <div
        id="scormContentLayout"
        ref={contentElement}
        className="m-portlet m-portlet--rounded"
        style={{ position: 'relative', marginBottom: !isLoading && scormLoaded ? '5.5rem' : '2rem' }}
      >
        <div className="m-portlet__body course-lesson-container">
          {!selectedCertificate && (
          <div className="scorm-content">
            {!isLoading && scormLoaded && (
              <>
                <CustomIframe
                  id={frameId}
                  ref={frameIdEl}
                  title={title}
                  allow="microphone; camera"
                  style={{
                    order: 0,
                    height: '100%',
                    width: '100%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                  src={urlUser}
                  allowFullScreen
                  frameBorder="0"
                  onReloadPage={() => {
                    setTimeout(() => {
                      window.location.reload();
                    }, 5000);
                  }}
                />
                <div className="buttonFullScreen">
                  <button type="button" className="btn m-btn--pill btn-outline-dynamic" onClick={handleClickFullScreen}>
                    <i className="fa fa-expand" />
                    {t('academy.detail.scorm.fullscreen')}
                  </button>
                  {showCloseCourse && !urlUser.includes('/completed') ?
                    <button
                      ref={closeCourseBtnRef}
                      type="button"
                      className="btn m-btn--pill btn-dynamic"
                      onClick={handleCloseCourse}
                    >
                      <i className="fa fa-check" />
                      {t('academy.scorm.btn.close')}
                    </button> : null}
                </div>
              </>
            )}
            {!isLoading && !scormLoaded && !showEnrollOverlay && (
              <div className={`enroll-overlay ${isFromSession ? 'enroll-overlay-session' : ''}`}>
              <h2 className="enroll-title">
                  {getTitleWelcomeScreen(course.status)}
                </h2>
                <p className="enroll-message">
                  {course.status === 'finished'
                    ? t('academy.detail.scorm.message_restart')
                    : t('academy.detail.scorm.message')}
                </p>
                {course.status === 'finished'
                  ? (
                    <>
                      <button type="button" className="btn m-btn--pill btn-dynamic" onClick={handleClickRestartCourse}>
                        {t('academy.detail.scorm.button_restart')}
                      </button>
                      <br />
                      <button type="button" className="btn m-btn--pill btn-dynamic" onClick={handleClickLoadCourse}>
                        {t('academy.detail.scorm.button')}
                      </button>
                    </>
                  )
                  : (
                    <button type="button" className="btn m-btn--pill btn-dynamic" onClick={handleClickLoadCourse}>
                      {t('academy.detail.scorm.button')}
                    </button>
                  )
                }
              </div>
            )}
          </div>
          )}
          {showEnrollOverlay && (
            <div className="enroll-overlay">
              <h2 className="enroll-title">{t('academyDetails_enrollToday')}</h2>
              <p className="enroll-message">
                {t('academyDetails_enrollMessage')}
              </p>
              <EnrollButton onClick={onEnroll} course={course} />
            </div>
          )}
          {selectedCertificate && (
            <div>
              <div className="row">
                <div className="col text-center">
                  {course.status === 'finished'
                    ? (
                      <div className="row">
                        <div className="col-md-12">
                          <button
                            type="button"
                            className="col-md-5 btn m-btn--pill btn-dynamic"
                            onClick={handleClickRestartCourse}
                            style={{
                              marginRight: 20,
                            }}
                          >
                            {t('academy.detail.scorm.button_restart')}
                          </button>
                          <button
                            type="button"
                            className="col-md-5 btn m-btn--pill btn-dynamic"
                            onClick={handleClickLoadCourse}
                            style={{
                              marginLeft: 20,
                            }}
                          >
                            {t('academy.detail.scorm.button')}
                          </button>
                        </div>
                      </div>
                    )
                    : (
                      <button type="button" className="btn m-btn--pill btn-dynamic" onClick={handleClickLoadCourse}>
                        {t('academy.detail.scorm.button')}
                      </button>
                    )
                  }
                </div>
              </div>
              <br />
              <br />
              <CertificateDisplay
                certificate={selectedCertificate}
                course={course}
                typeCertificate="course"
                appProducts={appProducts}
                journeyContext={journeyContext}
                sessionContext={sessionContext}
                onPrint={() => setShowPrintModal(true)}
                onEmail={() => setShowEnrollModal(true)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

ScormDetail.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  course: PropTypes.shape(CourseProp.propType).isRequired,
  showEnrollOverlay: PropTypes.bool,
  onEnroll: PropTypes.func,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType)).isRequired,
  shouldScrollContent: PropTypes.bool,
  inJourney: PropTypes.bool,
  isFromSession: PropTypes.bool,
  sessionContext: PropTypes.string,
  journeyContext: PropTypes.string,
};

ScormDetail.defaultProps = {
  showEnrollOverlay: false,
  onEnroll: null,
  shouldScrollContent: false,
  inJourney: false,
  isFromSession: false,
  sessionContext: null,
  journeyContext: null,
};

export default compose(
  translate,
  withCSRF,
)(ScormDetail);
